/**
 * The frame every page outside a room sits in.
 *
 * There were five: the front page had a masthead and no bar, a community page
 * a sticky bar at one width, its admin screen a plain bar at another, the
 * legal pages a smaller masthead, and the studio a bar of its own with a text
 * "Sign out" where every other page had a face. Moving between them read as
 * leaving the site, which is the one thing a frame exists to prevent.
 *
 * So it is ONE station sign, the same at every stop: the wordmark home, the
 * line you are on, and you.
 *
 * ONE LINE COLOUR, tomo's own. It used to sit on a strip of all ten channel
 * hues, and that read as decoration because it was: on tomo a hue means a
 * channel, and every hue at once means none of them. The brand blue is the
 * one colour that means tomo itself, so it is the only one the frame wears.
 *
 * One width, too. Content that wants a reading measure (the legal pages) or a
 * narrower form (community admin) narrows INSIDE the frame and keeps its left
 * edge, rather than every page re-centring on a width of its own.
 */
import type { ReactNode } from 'react';
import { AccountMenu } from './AccountMenu.jsx';
import { Link } from './primitives.jsx';
import { isStudio } from '../lib/router.js';
import type { ViewerState } from '../lib/viewer.js';

export interface Crumb {
  readonly label: string;
  /** Absent on the page you are on, which is not a link to itself. */
  readonly href?: string;
}

export function SiteFrame({
  viewer, trail = [], tagline, actions, below, children,
}: {
  readonly viewer: ViewerState;
  /** Where you are, after `tomo`. The last entry is this page. */
  readonly trail?: readonly Crumb[];
  /** Said beside the wordmark where there is no trail — the front page. */
  readonly tagline?: string;
  /** Page controls that belong in reach while scrolling. */
  readonly actions?: ReactNode;
  /** A second row under the bar, such as the studio's tabs. */
  readonly below?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="flex min-h-screen flex-col bg-ink-950 text-ink-50">
      {/*
        STICKY everywhere, not only where a page needed it. It was sticky on
        the community page because its Watch button is assembled further down;
        a bar that stays on one page and scrolls away on the next is exactly
        the inconsistency this component exists to remove.

        Solid rather than translucent: a blur behind the bar is a light effect,
        and it also made the bar the containing block for fixed descendants
        (see AccountMenu's portal).
      */}
      <header className="sticky top-0 z-20 border-b-[3px] border-brand-400 bg-ink-950">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-3 px-4">
          <a
            href="/"
            className="text-[22px] leading-none font-extrabold tracking-[-0.045em] text-ink-50 hover:text-brand-300"
          >
            tomo
          </a>
          {trail.length > 0 ? (
            <nav aria-label="You are here" className="flex min-w-0 items-center gap-2 text-sm">
              {trail.map((crumb, i) => (
                <span key={`${crumb.label}-${String(i)}`} className="flex min-w-0 items-center gap-2">
                  <span aria-hidden className="text-ink-700">/</span>
                  {crumb.href !== undefined ? (
                    /* Inside the studio a crumb is a client-side route; anywhere
                       else it is a different app and has to load as one. */
                    isStudio(crumb.href) ? (
                      <Link href={crumb.href} className="truncate text-ink-400 hover:text-ink-50">
                        {crumb.label}
                      </Link>
                    ) : (
                      <a href={crumb.href} className="truncate text-ink-400 hover:text-ink-50">
                        {crumb.label}
                      </a>
                    )
                  ) : (
                    <span aria-current="page" className="truncate font-semibold text-ink-50">
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          ) : tagline !== undefined ? (
            <p className="hidden truncate text-sm text-ink-400 sm:block">{tagline}</p>
          ) : null}
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {actions}
            <AccountMenu viewer={viewer} />
          </span>
        </div>
        {below}
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8">{children}</main>

      <footer className="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-x-4 gap-y-1 border-t border-ink-900 px-4 py-6 text-xs text-ink-700">
        <a href="/live/" className="text-ink-400 transition hover:text-brand-300">
          Open an empty room
        </a>
        <span className="ml-auto flex gap-4">
          <a href="/about" className="text-ink-400 transition hover:text-brand-300">About</a>
          <a href="/privacy" className="text-ink-400 transition hover:text-brand-300">Privacy</a>
          <a href="/terms" className="text-ink-400 transition hover:text-brand-300">Terms</a>
        </span>
      </footer>
    </div>
  );
}
